import { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import _find from 'lodash.find';
import { getFormattedNumberStr } from '../../utils/number';
import { useReportDetailStore } from '.';
import { EditableValue, StaticValue, SummaryLabel, SummaryWrapper, ValueWrapper } from './common';

function DivisionSummary({ data, onUpdate }: { data: any; onUpdate: any }) {
  const [divisionItem, setDivisionItem] = useState<any>(null);
  const [isDivisionItemUpdated, setDivisionItemUpdated] = useState<boolean>(false);
  const [millItem, setMillItem] = useState<any>(null);
  const [isMillItemUpdated, setMillItemUpdated] = useState<boolean>(false);
  const { updatedCells } = useReportDetailStore();
  const { userPermissions } = useReportDetailStore();

  const getDefaultItemData = (sourceData: any) => ({
    metricId: sourceData.metricId,
    value: sourceData.value,
    oldValue: sourceData.oldValue,
    metricUnit: sourceData.metricUnit,
    formula: sourceData.formula,
    targetType: sourceData.targetType,
    createdAt: sourceData.createdAt,
    createdBy: sourceData.createdBy,
    reasonId: sourceData.reasonId,
  });

  useEffect(() => {
    if (data) {
      const { millData } = data;

      setDivisionItem(getDefaultItemData(data));
      setMillItem(getDefaultItemData(millData));
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      const divisionUpdatedValue = _find(
        updatedCells,
        (i: any) => i.metricId === divisionItem?.metricId && i.targetType === divisionItem?.targetType,
      );
      const millUpdatedValue = _find(
        updatedCells,
        (i: any) => i.metricId === millItem?.metricId && i.targetType === millItem?.targetType,
      );

      if (divisionUpdatedValue) {
        setDivisionItemUpdated(true);
        setDivisionItem((prev: any) => ({
          ...prev,
          ...divisionUpdatedValue,
        }));
      } else {
        setDivisionItem(getDefaultItemData(data));
        setDivisionItemUpdated(false);
      }

      if (millUpdatedValue) {
        setMillItemUpdated(true);
        setMillItem((prev: any) => ({
          ...prev,
          ...millUpdatedValue,
        }));
      } else {
        const { millData } = data;
        setMillItem(getDefaultItemData(millData));
        setMillItemUpdated(false);
      }
    }
  }, [data, updatedCells]);

  if (!divisionItem || !millItem) {
    return null;
  }

  return (
    <SummaryWrapper>
      <div>
        <SummaryLabel>Mill annual target:</SummaryLabel>
        <ValueWrapper>
          {userPermissions.canSubmitTarget ? (
            <EditableValue
              onClick={() =>
                onUpdate({
                  data: {
                    value: divisionItem.value,
                    oldValue: divisionItem.oldValue,
                    metricUnit: divisionItem.metricUnit,
                    metricId: divisionItem.metricId,
                    targetType: divisionItem.targetType,
                    isDivisionalTarget: true,
                    reasonId: divisionItem.reasonId,
                  },
                })
              }
              isUpdated={isDivisionItemUpdated}
            >
              <i className="pi pi-pencil" />
              {getFormattedNumberStr(Math.round(divisionItem.value), divisionItem.metricUnit)}
            </EditableValue>
          ) : (
            <StaticValue>{getFormattedNumberStr(Math.round(divisionItem.value), divisionItem.metricUnit)}</StaticValue>
          )}
        </ValueWrapper>
      </div>
      <div>
        <SummaryLabel>Created By:</SummaryLabel>
        <span>
          {divisionItem.createdBy} (
          {divisionItem.createdAt
            ? DateTime.fromISO(divisionItem.createdAt).toLocaleString({
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
              })
            : '-'}
          )
        </span>
      </div>

      {millItem && (
        <>
          <div>
            <SummaryLabel>Baseline:</SummaryLabel>
            <ValueWrapper>
              {userPermissions.canSubmitTarget ? (
                <EditableValue
                  onClick={() =>
                    onUpdate({
                      data: {
                        value: millItem.value,
                        oldValue: millItem.oldValue,
                        metricUnit: millItem.metricUnit,
                        metricId: millItem.metricId,
                        targetType: millItem.targetType,
                        isDivisionalTarget: true,
                        reasonId: millItem.reasonId,
                      },
                    })
                  }
                  isUpdated={isMillItemUpdated}
                >
                  <i className="pi pi-pencil" />
                  {getFormattedNumberStr(Math.round(millItem.value), millItem.metricUnit)}
                </EditableValue>
              ) : (
                <StaticValue>{getFormattedNumberStr(Math.round(millItem.value), millItem.metricUnit)}</StaticValue>
              )}
            </ValueWrapper>
          </div>
          <div>
            <SummaryLabel>Created By:</SummaryLabel>
            {millItem.createdBy ? (
              <span>
                {millItem.createdBy} (
                {millItem.createdAt
                  ? DateTime.fromISO(millItem.createdAt).toLocaleString({
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                      second: 'numeric',
                    })
                  : '-'}
                )
              </span>
            ) : (
              <span>-</span>
            )}
          </div>
        </>
      )}
    </SummaryWrapper>
  );
}

export default DivisionSummary;
